import { Loader2 } from "lucide-react";
import type { ComponentProps, ReactNode } from "react";

import { cn } from "@/lib/cn";

export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger" | "outline";
export type ButtonSize = "sm" | "md" | "lg";

/**
 * Estados del botón según el "Button State Matrix" del manual de marca:
 *
 *   normal  → Azul Eléctrico
 *   hover   → Azul Marino
 *   pressed → azul intermedio
 *
 * El manual dibuja el estado "pressed" igual que el "normal". Se usa un tono
 * intermedio (`--color-electric-pressed`) porque un botón que no cambia al
 * presionarlo hace que el cajero lo pulse dos veces y cobre dos veces.
 */
const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  primary: "bg-electric text-on-electric hover:bg-electric-hover active:bg-electric-pressed",
  secondary: "bg-snow text-carbon border border-line hover:bg-surface active:bg-line",
  ghost: "bg-transparent text-carbon hover:bg-line/60 active:bg-line",
  danger: "bg-danger text-white hover:bg-danger-hover active:bg-danger-hover",
  outline: "bg-transparent border border-white/20 text-white hover:bg-white/10 active:bg-white/20",
};

const SIZE_CLASSES: Record<ButtonSize, string> = {
  sm: "h-9 gap-1.5 px-3 text-sm",
  // `lg` es el tamaño de cobro: 18px y negrita, que es lo que hace que el
  // texto blanco sobre Azul Eléctrico cumpla contraste AA (3.98:1 → AA de
  // texto grande). Con texto pequeño ese mismo azul NO cumple.
  md: "h-11 gap-2 px-4 text-sm",
  lg: "h-14 gap-2.5 px-6 text-lg font-bold",
};

export interface ButtonProps extends ComponentProps<"button"> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Muestra el spinner y bloquea el botón: evita el doble cobro. */
  loading?: boolean;
  icon?: ReactNode;
  fullWidth?: boolean;
}

export function Button({
  variant = "primary",
  size = "md",
  loading = false,
  icon,
  fullWidth = false,
  className,
  children,
  disabled,
  type = "button",
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      disabled={Boolean(disabled) || loading}
      aria-busy={loading || undefined}
      className={cn(
        "inline-flex items-center justify-center rounded-msr font-semibold transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-electric focus-visible:ring-offset-2",
        "disabled:cursor-not-allowed disabled:opacity-50",
        VARIANT_CLASSES[variant],
        SIZE_CLASSES[size],
        fullWidth && "w-full",
        className,
      )}
      {...rest}
    >
      {loading ? <Loader2 aria-hidden className="size-4 animate-spin" /> : icon}
      {children}
    </button>
  );
}
